import moment from 'moment';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import * as XLSXStyle from 'xlsx-js-style';

import strings from './MsgHelpers';
import {
  DocExtension,
  LogoOrgExtension,
  NCsCsExtension,
} from '../enums/FileExtensions';

import Api from '../Api';

// Builds one styled worksheet (title banner + header + data rows) for exportExcelStyledReport.
// Kept outside `utils` since it's only an implementation detail of that export.
const buildStyledSheet = ({
  title,
  headers,
  rows,
  colWidths,
  leftAlignColIndices = [],
}) => {
  const styleTitle = {
    fill: { fgColor: { rgb: 'BE4439' } },
    font: { bold: true, color: { rgb: 'FFFFFF' } },
    alignment: { horizontal: 'center', vertical: 'center' },
  };
  const styleHeader = {
    font: { bold: true },
    alignment: { horizontal: 'center', vertical: 'center' },
  };
  const styleData = {
    alignment: { horizontal: 'center', vertical: 'center' },
  };
  const styleDataLeft = {
    alignment: { horizontal: 'left', vertical: 'center' },
  };
  const borderOutline = { style: 'thin', color: { rgb: '000000' } };
  const borderTitle = { style: 'thin', color: { rgb: 'BE4439' } };

  const lastCol = headers.length - 1;
  const wsData = [[title], headers, ...rows];
  const lastRow = wsData.length - 1;

  const ws = XLSXStyle.utils.aoa_to_sheet(wsData);
  ws['!merges'] = ws['!merges'] || [];
  ws['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } });

  ws['A1'].s = styleTitle;
  for (let r = 0; r <= lastRow; r++) {
    for (let c = 0; c <= lastCol; c++) {
      const ref = XLSXStyle.utils.encode_cell({ r, c });
      if (!ws[ref]) continue;
      if (r === 1) ws[ref].s = styleHeader;
      else if (r > 1)
        ws[ref].s = leftAlignColIndices.includes(c) ? styleDataLeft : styleData;
      const onTop = r === 0,
        onBottom = r === lastRow,
        onLeft = c === 0,
        onRight = c === lastCol;
      if (onTop || onBottom || onLeft || onRight) {
        const outline = r === 0 ? borderTitle : borderOutline;
        const border = {};
        if (onTop) border.top = outline;
        if (onBottom) border.bottom = outline;
        if (onLeft) border.left = outline;
        if (onRight) border.right = outline;
        ws[ref].s = { ...(ws[ref].s || {}), border };
      }
    }
  }

  if (Array.isArray(colWidths) && colWidths.length)
    ws['!cols'] = colWidths.map((wch) => ({ wch }));

  return ws;
};

const utils = {
  errorDates: ['Data inválida', '-', '', null, undefined],

  isNullOrEmpty: (value) => {
    if (value === null || value === undefined || value === '') {
      return true;
    }
    if (typeof value === 'string' && value.trim() === '') {
      return true;
    }
    if (typeof value === 'object' && !Array.isArray(value)) {
      return Object.keys(value).length === 0;
    }
    if (typeof value === 'string' || Array.isArray(value)) {
      return value.length === 0;
    }
    return false;
  },

  getNowDateTimeFormatted: () => {
    var currentDate = new Date();

    var formattedDate =
      currentDate.getFullYear() +
      '-' +
      ('0' + (currentDate.getMonth() + 1)).slice(-2) +
      '-' +
      ('0' + currentDate.getDate()).slice(-2) +
      ' ' +
      ('0' + currentDate.getHours()).slice(-2) +
      ':' +
      ('0' + currentDate.getMinutes()).slice(-2) +
      ':' +
      ('0' + currentDate.getSeconds()).slice(-2);

    return formattedDate;
  },

  getMonthName(monthNumber) {
    const months = [
      'Janeiro',
      'Fevereiro',
      'Março',
      'Abril',
      'Maio',
      'Junho',
      'Julho',
      'Agosto',
      'Setembro',
      'Outubro',
      'Novembro',
      'Dezembro',
    ];

    if (monthNumber < 1 || monthNumber > 12) {
      return '-';
    }

    return months[monthNumber - 1];
  },

  blobToString: async (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result);
      };
      reader.onerror = reject;
      reader.readAsBinaryString(blob);
    });
  },

  closeSidebar(action) {
    const sidebar = document.getElementById('sidebar');
    const header = document.getElementById('header');
    const main =
      document.getElementById('main') ||
      document.getElementById('mainDashUser');
    const footer = document.getElementById('footer');

    if (!action || action === 'toggle') {
      sidebar?.classList.toggle('fechar');
      header?.classList.toggle('abrir');
      main?.classList.toggle('open');
      footer?.classList.toggle('open');
    } else if (action === 'close') {
      if (sidebar) {
        sidebar.classList.add('fechar');
      }
      if (header) {
        header.classList.remove('abrir');
      }
      if (main) {
        main.classList.remove('open');
      }
      if (footer) {
        footer.classList.remove('open');
      }
    }
  },

  setSessionTimeout() {
    const tokenString = sessionStorage.getItem('token');
    const SESSION_INTERVAL = process.env.REACT_APP_SESSION_INTERVAL;

    if (tokenString) {
      const currentTime = new Date();
      const expirationTime = new Date(currentTime);
      expirationTime.setMinutes(
        currentTime.getMinutes() + Number(SESSION_INTERVAL)
      );
      sessionStorage.setItem('sessionExpiration', expirationTime.toString());
    }
  },

  checkFile: (bytes, fileFormat, decimals = 2, mbMax = 131072) => {
    if (bytes === 0)
      return { description: '0 Bytes', size: bytes, valid: false };

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const formats = [
      'application/pdf',
      'image/png',
      'image/jpg',
      'image/jpeg',
      'video/mp4',
    ];

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    let siz = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));
    let desc = siz + ' ' + sizes[i];
    let val = i <= 2 && bytes < mbMax * k;
    let format = formats.indexOf(fileFormat) >= 0;

    return {
      description: desc,
      size: siz,
      validSize: val,
      validFormat: format,
    };
  },

  checkFileDocType: (file) => {
    const maxSize = 128 * 1024 * 1024; // 128MB
    const formats = DocExtension;

    if (file.size > maxSize) {
      return {
        isValid: false,
        message: strings.O_TAMANHO_DO_ARQUIVO_EXCEDE_O_LIMITE_DE_128_MB,
      };
    } else if (!formats.includes(file.name.split('.').pop().toLowerCase())) {
      return {
        isValid: false,
        message: strings.TIPO_DE_ARQUIVO_NAO_SUPORTADO,
      };
    }

    return {
      isValid: true,
      message: '',
    };
  },

  checkFileNCType: (file) => {
    const maxSize = 25 * 1024 * 1024; // 25MB
    const formats = NCsCsExtension;

    if (file.size > maxSize) {
      return {
        isValid: false,
        message:
          strings.O_TAMANHO_DO_ARQUIVO_ULTRAPASSA_O_LIMITE_DE_25_MB_ESTABELECIDO_PELO_GMAIL,
      };
    } else if (!formats.includes(file.name.split('.').pop().toLowerCase())) {
      return {
        isValid: false,
        message: strings.TIPO_DE_ARQUIVO_NAO_SUPORTADO,
      };
    }

    return {
      isValid: true,
      message: '',
    };
  },

  checkFileLogoType: (file) => {
    const formats = LogoOrgExtension;

    if (!formats.includes(file.name.split('.').pop().toLowerCase())) {
      return {
        isValid: false,
        message: strings.TIPO_DE_ARQUIVO_NAO_SUPORTADO,
      };
    }

    return {
      isValid: true,
      message: '',
    };
  },

  validatePasswordRequirements: (password) => {
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(
      password
    );
    const hasMinLength = password.length >= 8;

    return {
      hasUpperCase,
      hasLowerCase,
      hasNumber,
      hasSpecialChar,
      hasMinLength,
      isValid:
        hasUpperCase &&
        hasLowerCase &&
        hasNumber &&
        hasSpecialChar &&
        hasMinLength,
    };
  },

  downloadFileDoc: (file) => {
    try {
      Swal.fire({
        backdrop: true,
        allowOutsideClick: false,
        didOpen: async () => {
          Swal.showLoading();
          const res = await Api.downloadDocument(file.document_id);
          if (res) {
            const extension =
              '.' + file.document_path.split('.').pop().toLowerCase();

            try {
              const blob = new Blob([res]);
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              const originalFileName = `${file.document_name.replace(' ', '_')}_${file.document_emitted.replace(' ', '_')}${extension}`;
              link.href = url;
              link.setAttribute('download', originalFileName);
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            } catch (error) {
              console.error('Erro ao realizar a operação:', error.message);
            }
          }
          Swal.close();
        },
      });
    } catch (error) {
      console.error('Erro ao fazer o download:', error.message);
    }
  },

  getFileExtension(filename) {
    return filename.split('.').pop().toLowerCase();
  },

  removerAcentuacao(str) {
    // Translitera caracteres acentuados para seus equivalentes não acentuados
    const comAcento =
      'ÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖØÙÚÛÜÝÞßàáâãäåæçèéêëìíîïðñòóôõöøùúûüýþÿ';
    const semAcento =
      'AAAAAAACEEEEIIIIDNOOOOOOUUUUYBsaaaaaaaceeeeiiiidnoooooouuuuyty';

    return str
      .split('')
      .map((char) => {
        const index = comAcento.indexOf(char);
        return index !== -1 ? semAcento[index] : char;
      })
      .join('')
      .replace(/[^a-zA-Z0-9 ]/g, '') // Remove caracteres não alfanuméricos
      .replace(/ /g, '_'); // Substitui espaços por underscores
  },

  formatDate(date, formato) {
    // Mapeia as partes da data
    const partes = {
      d: String(date.getDate()).padStart(2, '0'), // Dia
      m: String(date.getMonth() + 1).padStart(2, '0'), // Mês
      y: date.getFullYear().toString().slice(-2), // Ano (últimos dois dígitos)
      Y: date.getFullYear(), // Ano (completo)
      H: String(date.getHours()).padStart(2, '0'), // Hora
      i: String(date.getMinutes()).padStart(2, '0'), // Minuto
      s: String(date.getSeconds()).padStart(2, '0'), // Segundo
    };

    // Substitui os placeholders no formato pela data correspondente
    return formato.replace(/([dmyYHis])/g, (match) => partes[match] || match);
  },

  formatDateLforDefault(dateL) {
    if (dateL == null || dateL === '' || dateL === '-') {
      return '';
    }
    const s = String(dateL).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
      const iso = moment(s.slice(0, 10), 'YYYY-MM-DD', true);
      return iso.isValid() ? iso.format('YYYY-MM-DD') : '';
    }
    const parsed = moment(s, 'L', true);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
  },

  downloadFilePdf: (response, fileName) => {
    if (response) {
      const blob = new Blob([response]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const originalFileName = fileName;

      link.href = url;
      link.setAttribute('download', originalFileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  },

  calculateRiskClassification: (percentValue) => {
    if (percentValue >= 80 && percentValue <= 100) {
      return {
        color: 'success',
        label: 'BAIXO RISCO',
      };
    } else if (percentValue > 30 && percentValue < 80) {
      return {
        color: 'warning',
        label: 'MÉDIO RISCO',
      };
    } else {
      return {
        color: 'danger',
        label: 'ALTO RISCO',
      };
    }
  },

  hideBrokenImg: (event) => {
    event.target.style.display = 'none';
  },

  exportExcel: (data, filename = 'dados.xlsx') => {
    const [headers, ...rows] = data;

    const jsonData = rows.map((row) => {
      const obj = {};
      headers.forEach((header, index) => {
        obj[header] = row[index];
      });
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(jsonData, { header: headers });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, filename);
  },

  exportExcelStyledReport: ({ filename, sheets }) => {
    const wb = XLSXStyle.utils.book_new();

    sheets.forEach((sheet) => {
      const ws = buildStyledSheet(sheet);
      XLSXStyle.utils.book_append_sheet(wb, ws, sheet.sheetName || 'Relatório');
    });

    XLSXStyle.writeFile(wb, filename);
  },

  badgeClassConfig: (status) => {
    switch (status.toUpperCase()) {
      case 'INOPERANTE':
      case 'INCONFORME':
        return 'bg-danger';
      case 'EM MANUTENÇÃO':
        return 'bg-warning text-dark';
      default:
        return 'bg-success';
    }
  },
};

export default utils;
